import React from "react";
import { USER_TYPES } from "constants/userTypes";

// space pages

// Loading fallback — no MUI so lazy route shell stays out of vendors-mui for marketing paths
const LoadingFallback = () => (
  <div className="global-spinner-container" role="status" aria-label="Loading">
    <div className="global-spinner" />
  </div>
);

const LinkedInCallback = React.lazy(() =>
  import("hooks/LinkedIn").then((m) => ({ default: m.LinkedInCallback }))
);
const DepartmentShare = React.lazy(
  () => import("views/space/Share/Department"),
);
const JobShare = React.lazy(() => import("views/space/Share/Job"));

// const TechnicalInterviewStart = React.lazy(
//   () => import("views/space/TechnicalInterview"),
// ); // Technical section removed

const ScreeningStart = React.lazy(() => import("views/space/Screening"));
const InterviewStart = React.lazy(() => import("views/space/Interview"));

// website pages
const HomePage = React.lazy(() => import("views/space/website/Home/HomePage"));
const JobsPage = React.lazy(() => import("views/space/website/Home/JobsPage"));
const ProgrammaticJobPage = React.lazy(() => import("views/space/website/Pages/ProgrammaticJobPage"));
const UserDashboardSpacePage = React.lazy(
  () => import("views/dashboard/Space/SpacePage"),
);
const CareerPageWrapper = React.lazy(
  () => import("views/space/CareerPageWrapper"),
);
const CandidateJobsPage = React.lazy(
  () => import("views/dashboard/Candidates/Jobs/JobsPage"),
);
const CandidateConnectionsPage = React.lazy(
  () => import("views/dashboard/Candidates/Connections/ConnectionsListPanelV2"),
);

// dashboard pages
const profilePage = React.lazy(() => import("views/dashboard/profilePage"));
const userProfilePage = React.lazy(
  () => import("views/dashboard/UserProfilePage"),
);
const departmentList = React.lazy(
  () => import("views/dashboard/department/departmentList"),
);

// Plan selection page (Super Admin only - for first-time subscription)
const PlanSelectionPage = React.lazy(
  () => import("views/dashboard/Plans"),
);

const ConnectionsListPanel = React.lazy(
  () => import("views/dashboard/Connections/ConnectionsListPanel"),
);

const JobEditScreen = React.lazy(
  () => import("views/dashboard/job/JobEditScreen").then(module => {
    // Preload rich text editor dependencies when job editing loads
    import("draft-js").catch(() => { });
    import("react-draft-wysiwyg").catch(() => { });
    import("draftjs-to-html").catch(() => { });
    import("html-to-draftjs").catch(() => { });
    return module;
  })
);

const JobAnalytics = React.lazy(
  () => import("views/dashboard/job/JobAnalyticsAdvanced"),
);
const CandidateRecommendationsScreen = React.lazy(
  () => import("views/dashboard/job/Recommendations/CandidateList"),
);

const KnowledgeBase = React.lazy(() => import("views/dashboard/Knowledge"));
const TrainingList = React.lazy(
  () => import("views/dashboard/Knowledge/TrainingList"),
);
const Interactions = React.lazy(
  () => import("views/dashboard/Knowledge/Interactions"),
);
const CompanyFeedbackPage = React.lazy(
  () => import("views/dashboard/Feedback/FeedbackPage"),
);
const TalentPoolPage = React.lazy(
  () => import("views/dashboard/TalentPool/TalentPoolPage"),
);
// const ResetPassword = React.lazy(() =>
//   import("views/space/LoginScreen/ResetPassword")
// );
const BotList = React.lazy(() => import("views/dashboard/Knowledge/BotList"));
// Dashboard page – charts use lightweight Canvas/SVG (no recharts dependency)
const DashboardPage = React.lazy(() => import("views/dashboard/insights"));

const Records = React.lazy(() => import("views/dashboard/Records"));
const AllNotifications = React.lazy(
  () => import("views/dashboard/AllNotifications"),
);
const AllEmails = React.lazy(() => import("views/dashboard/AllEmails"));
const CandidatePage = React.lazy(
  () => import("views/dashboard/candidate/CandidatePage"),
);
const IntegrationPage = React.lazy(
  () => import("views/dashboard/settings/IntegrationPage"),
);
const IntegrationMethodsPage = React.lazy(
  () => import("views/dashboard/settings/IntegrationMethodsPage"),
);
const OtherSettingsPage = React.lazy(
  () => import("views/dashboard/settings/otherSettings"),
);
const PermissionsPage = React.lazy(
  () => import("views/dashboard/permissions/PermissionsPage"),
);
const CompanyRegistration = React.lazy(
  () => import("views/dashboard/auth/CompanyRegistration"),
);

const Login = React.lazy(() => import("views/auth/LoginV2"));
const CompanyUserRegister = React.lazy(
  () => import("views/auth/CompanyUserRegister"),
);

const CandidateRegistration = React.lazy(
  () => import("views/dashboard/auth/CandidateRegistration"),
);
const CandidateLogin = React.lazy(
  () => import("views/dashboard/auth/CandidateLogin"),
);

const ForgotPassword = React.lazy(() => import("views/auth/ForgotPassword"));
const ResetPassword = React.lazy(() => import("views/auth/ResetPassword"));
const ScorecardFeedbackPage = React.lazy(
  () => import("views/scorecard/ScorecardFeedbackPage"),
);
const CandidateResetPassword = React.lazy(
  () => import("views/dashboard/auth/CandidateResetPassword"),
);
const ScreenRecording = React.lazy(
  () => import("views/space/Recording/ScreenRecording"),
);
const RolePermissions = React.lazy(
  () => import("views/dashboard/permissions/RolePermissions"),
);
const CreateRole = React.lazy(
  () => import("views/dashboard/permissions/CreateRole"),
);

const RolesList = React.lazy(() => import("views/dashboard/RolesList"));
const JobList = React.lazy(() => import("views/dashboard/department/JobList"));
const GlobalJobsList = React.lazy(() => import("views/dashboard/GlobalJobsList"));


const jobAssistant = React.lazy(
  () => import("views/dashboard/Knowledge/BotList/jobAssistant"),
);

const jobAssistantPage = React.lazy(
  () => import("views/dashboard/Knowledge/BotList/jobAssistantPage"),
);

const departmentAssistant = React.lazy(
  () => import("views/dashboard/Knowledge/BotList/departmentAssistant"),
);

const depAssistantPage = React.lazy(
  () => import("views/dashboard/Knowledge/BotList/depAssistantPage"),
);

const TaskDetails = React.lazy(
  () => import("views/dashboard/inbox/messagePanel/TaskDetails"),
);
const InboxScreen = React.lazy(
  () => import("views/dashboard/inbox/inboxScreen"),
);
const InterviewsDashboard = React.lazy(
  () => import("views/dashboard/interviews/InterviewsDashboard")
);
const JobPipeline = React.lazy(() => import("views/dashboard/pipeline/JobPipeline"));
const AccessCodePage = React.lazy(
  () => import("views/dashboard/settings/otherSettings/AccessCodePage"),
);
const actionPage = React.lazy(
  () => import("views/dashboard/settings/otherSettings/actionPage"),
);

//candidate Login
const CandidateInsight = React.lazy(
  () => import("views/dashboard/Candidates/Insights/CandidateInsight"),
);
const CandidateInbox = React.lazy(
  () => import("views/dashboard/Candidates/Inbox/CandidateInbox"),
);
// const CandidateSceneSetup = React.lazy(
//   () => import("views/dashboard/Candidates/SceneSetup/CandidateSceneSetup"),
// );
const CandidateSceneSetting = React.lazy(
  () => import("views/dashboard/Candidates/SceneSetup/CandidateSceneSetting"),
);
const CandidateSettings = React.lazy(
  () => import("views/dashboard/Candidates/Settings/CandidateSettings"),
);

const ScenePreview = React.lazy(() => import("views/dashboard/ScenePreview"));

// Auth pages
const CreateAccount = React.lazy(() => import("views/auth/CreateAccount"));
const CandidateVerification = React.lazy(
  () => import("views/auth/CandidateVerification"),
);

const AuthHub = React.lazy(() => import("views/auth/AuthHub"));

// Layouts
const AdminLayout = React.lazy(() => import("layouts/Admin"));
const AuthLayout = React.lazy(() => import("layouts/Auth"));
const CandidateLayout = React.lazy(() => import("layouts/Candidate"));
const WebsiteLayout = React.lazy(() => import("layouts/Website/WebsiteLayout"));
const SpaceBasicLayout = React.lazy(() => import("layouts/Space/BasicLayout"));
const CompanySpaceLayout = React.lazy(
  () => import("layouts/Space/CompanySpaceLayout"),
);
const CompanyRecruiterVerify = React.lazy(
  () => import("views/dashboard/auth/CompanyOtpVerification"),
);
const TwoFactorVerify = React.lazy(
  () => import("views/dashboard/auth/TwoFactorVerify"),
);

// chat inbox
const ChatInboxScreen = React.lazy(() => import("views/dashboard/Chat/Inbox"));
const CandidateInboxScreen = React.lazy(() => import("views/candidate/Chat/CandidateInbox"));
const AppliedJobsPage = React.lazy(() => import("views/dashboard/Candidates/AppliedJobs/AppliedJobsPage"));
const CandidateFeedbackPage = React.lazy(() => import("views/dashboard/Candidates/Feedback/FeedbackPage"));
const MailInboxScreen = React.lazy(
  () => import("views/dashboard/Candidates/Mail/Inbox"),
);
const NotificationInboxScreen = React.lazy(
  () => import("views/dashboard/Candidates/Notification/Inbox"),
);

const AssistantScreen = React.lazy(() => import("views/dashboard/assistant"));

const flow = React.lazy(() => import("views/flow"));

const PaymentPage = React.lazy(() => import("views/dashboard/Payment"));

const UpgradePage = React.lazy(() => import("views/dashboard/Payment/upgrade"));

const successPage = React.lazy(
  () => import("views/dashboard/Payment/successPage"),
);

const trialPage = React.lazy(() => import("views/dashboard/Payment/trialPage"));

// Credit System pages
const CreditDashboard = React.lazy(() => import("views/dashboard/Credits/CreditDashboard"));
const BoosterPacks = React.lazy(() => import("views/dashboard/Credits/BoosterPacks"));
const BillingManagement = React.lazy(() => import("views/dashboard/Billing/BillingManagement"));

const AdminPage = React.lazy(
  () => import("views/admin/AdminPage"),
);

const CompanyAnalytics = React.lazy(
  () => import("views/admin/CompanyAnalytics"),
);

const SubmitReferences = React.lazy(() => import("views/public/SubmitReferences"));

const ImpersonateAutoLogin = React.lazy(() => import("views/admin/ImpersonateAutoLogin"));

const InterviewBookingPage = React.lazy(() => import("views/public/InterviewBookingPage"));

const routes = [
  {
    path: "/",
    component: HomePage,
    layout: WebsiteLayout,
    exact: true,
    publicAuth: true,
  },
  {
    path: "/jobs/:roleSlug-jobs-in-:citySlug",
    component: ProgrammaticJobPage,
    layout: WebsiteLayout,
    exact: true,
    publicAuth: true,
  },
  {
    path: "/auth/hub",
    component: AuthHub,
    layout: AuthLayout,
    exact: true,
    publicAuth: true,
  }, {
    path: "/create_account",
    component: CreateAccount,
    layout: AuthLayout,
    exact: true,
    publicAuth: true,
  },
  {
    path: "/submit-references",
    component: SubmitReferences,
    layout: AuthLayout,
    exact: true,
    publicAuth: true,
  },
  {
    path: "/interview/book/:token",
    component: InterviewBookingPage,
    layout: AuthLayout,
    exact: true,
    publicAuth: true,
  },
  // Technical interview route removed
  {
    path: "/start/screening/:companyId/:jobId/:candidateId",
    component: ScreeningStart,
    layout: CompanySpaceLayout,
  },
  {
    path: "/start/interview/:companyId/:jobId/:candidateId",
    component: InterviewStart,
    layout: CompanySpaceLayout,
  },
  {
    path: "/share/company/:companyId/department/:departmentId/job/:jobId",
    component: JobShare,
  },
  {
    path: "/share/company/:companyId/department/:departmentId",
    component: DepartmentShare,
  },
  {
    path: "/linkedin",
    component: LinkedInCallback,
    exact: true,
  },
  // end of candidate space routes
  {
    path: "/login",
    component: Login,
    layout: AuthLayout,
    exact: true,
    publicAuth: true,
  },
  {
    path: "/forgot/password",
    component: ForgotPassword,
    layout: AuthLayout,
    exact: true,
  },
  {
    path: "/reset/password",
    component: ResetPassword,
    layout: AuthLayout,
    exact: true,
  },
  {
    path: "/verify/candidate/:userId/:verificationCode",
    component: CandidateVerification,
    layout: AuthLayout,
  },
  {
    path: "/scorecard/feedback/:token",
    component: ScorecardFeedbackPage,
    layout: AuthLayout,
    exact: true,
    // No publicAuth: both logged-in and non-logged-in users must access this page.
    // publicAuth redirects authenticated users to /dashboard/insights.
  },
  // {
  //   path: "/reset/password",
  //   component: ResetPassword,
  //   layout: AuthLayout,
  //   exact: true,
  // },
  // {
  //   path: "/candidate/reset/password",
  //   component: CandidateResetPassword,
  //   layout: AuthLayout,
  //   exact: true,
  // },
  {
    path: "/registration/:company/invite/:inviteId",
    component: CompanyUserRegister,
  },
  // REMOVED: /register/company (CompanyRegistration) - deprecated in favor of /create_account which uses /users/create

  // candidate sidebar
  // {
  //   path: "/candidate/insights",
  //   name: "Insights",
  //   component: CandidateInsight,
  //   layout: CandidateLayout,
  //   type: "candidate-dashboard",
  //   exact: true,
  // },
  {
    path: "/screen/recording",
    // name: "Recording",
    component: ScreenRecording,
    layout: CandidateLayout,
    allowedRoles: [USER_TYPES.CANDIDATE],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/candidate/jobs",
    name: "Discover, Apply Jobs",
    component: CandidateJobsPage,
    layout: CandidateLayout,
    allowedRoles: [USER_TYPES.CANDIDATE],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/candidate/requests",
    name: "Job Invites",
    component: CandidateConnectionsPage,
    layout: CandidateLayout,
    allowedRoles: [USER_TYPES.CANDIDATE],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/candidate/chats",
    name: "Conversations",
    component: CandidateInboxScreen,
    layout: CandidateLayout,
    allowedRoles: [USER_TYPES.CANDIDATE],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/candidate/applied-jobs",
    name: "My Dashboard",
    component: AppliedJobsPage,
    layout: CandidateLayout,
    allowedRoles: [USER_TYPES.CANDIDATE],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/candidate/scene-setup",
    name: "Profile",
    component: CandidateSceneSetting,
    layout: CandidateLayout,
    allowedRoles: [USER_TYPES.CANDIDATE],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/scene/preview/type/:type/:id",
    component: ScenePreview,
  },
  {
    path: "/candidate/settings",
    name: "Settings",
    component: CandidateSettings,
    layout: CandidateLayout,
    allowedRoles: [USER_TYPES.CANDIDATE],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/candidate/feedback",
    name: "Share Feedback",
    component: CandidateFeedbackPage,
    layout: CandidateLayout,
    allowedRoles: [USER_TYPES.CANDIDATE],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  // {
  //   path: "/candidate/chats/:selectedUserId/:company_Ids",
  //   component: ChatInboxScreen,
  //   layout: CandidateLayout,
  //   type: "candidate-dashboard",
  //   exact: true,
  // },
  {
    path: "/candidate/chats/:selectedUserId",
    component: CandidateInboxScreen,
    layout: CandidateLayout,
    allowedRoles: [USER_TYPES.CANDIDATE],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/candidate/chats/:selectedUserId/:company_Ids",
    component: CandidateInboxScreen,
    layout: CandidateLayout,
    allowedRoles: [USER_TYPES.CANDIDATE],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },

  {
    path: "/candidate/AllEmails",
    component: AllEmails,
    layout: CandidateLayout,
    allowedRoles: [USER_TYPES.CANDIDATE],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/candidate/notifications",
    component: NotificationInboxScreen,
    layout: CandidateLayout,
    allowedRoles: [USER_TYPES.CANDIDATE],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },

  {
    path: "/scene/preview/type/:type/:id",
    component: ScenePreview,
  },
  {
    path: "/dashboard/insights",
    name: "Insights",
    component: DashboardPage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/spaces",
    name: "Candidates",
    component: UserDashboardSpacePage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
    // Actions are permission-gated separately
  },
  {
    path: "/dashboard/interviews",
    name: "Interviews",
    component: InterviewsDashboard,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/departments/:departmentId/job/edit/:jobId?/:tab?",
    component: JobEditScreen,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/departments/:departmentId/job/analytics/:jobId",
    component: JobAnalytics,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/departments/:departmentId/job/:jobId?/:tab?",
    component: CandidateRecommendationsScreen,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/departments/:departmentId",
    component: JobList,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },

  {
    path: "/dashboard/departments",
    name: "Departments",
    component: departmentList,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
    requiredPermission: "departments.view",  // V3 RBAC: Use granular permission
  },
  {
    path: "/dashboard/jobs",
    name: "Jobs",
    component: GlobalJobsList,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
    icon: "WorkIcon",
    requiredPermission: "jobs",
  },
  {
    path: "/dashboard/jobs/create",
    component: JobEditScreen,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/jobs/draft/:draftId",
    component: JobEditScreen,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/jobs/edit/:jobId?/:tab?",
    component: JobEditScreen,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/jobs/analytics/:jobId",
    component: JobAnalytics,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/connections",
    name: "Invites",
    component: ConnectionsListPanel,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
    // V3 RBAC: Invites requires departments.view permission (organizational authority)
    requiredPermission: "departments.view",
  },
  // inbox
  {
    path: "/dashboard/inbox/taskDetails/:jobId/:candidateId",
    component: TaskDetails,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/inbox/records/:jobId?/:candidateId?",
    component: InboxScreen,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/chat/inbox/:selectedUserId?",
    component: ChatInboxScreen,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  // Pipeline - Main inbox route that shows all departments
  {
    path: "/dashboard/inbox",
    component: CandidatePage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  // Job-specific pipeline (no dropdowns) - no name to hide from sidebar
  // IMPORTANT: This route must come BEFORE the general route to prevent conflicts
  {
    path: "/dashboard/inbox/job/:companyId/:departmentId/:jobId",
    component: JobPipeline,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/inbox/:departmentId?/:jobId?/:candidateId?",
    defaultUrl: "/inbox",
    component: CandidatePage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/candidate/:departmentId/:jobId/:candidateId",
    component: CandidatePage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/settings/profile",
    component: profilePage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/company/plans",
    component: PlanSelectionPage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
    // Note: Super Admin check is enforced in the component itself
  },
  {
    path: "/dashboard/settings/user-profile",
    component: userProfilePage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/settings/integrations",
    component: IntegrationPage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/settings/other-settings",
    component: OtherSettingsPage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/meet/oauth/redirect",
    component: AccessCodePage,
    exact: true,
  },
  {
    path: "/actions",
    component: actionPage,
    exact: true,
  },
  {
    path: "/dashboard/settings/permissions/users",
    component: PermissionsPage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/settings/permissions/settings",
    component: RolePermissions,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/settings/permissions/create",
    component: CreateRole,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/admin/auto-login",
    component: ImpersonateAutoLogin,
    exact: true,
  },
  {
    path: "/dashboard/settings/permissions/roles",
    component: RolesList,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },

  {
    path: "/dashboard/records/:candidateId/:jobId",
    component: Records,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/notifications",
    component: AllNotifications,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/AllEmails",
    component: AllEmails,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/feedback",
    name: "Feedback",
    component: CompanyFeedbackPage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/talent-pool",
    name: "Talent Pool",
    component: TalentPoolPage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },

  {
    path: "/dashboard/setup-ai/bots/department/:_departmentId/job/:jobId/assistant",
    component: jobAssistantPage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/setup-ai/bots/department/:_departmentId/job/:jobId",
    component: jobAssistant,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/setup-ai/bots/department/:_departmentId/assistant",
    component: depAssistantPage,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/dashboard/setup-ai/bots/department/:_departmentId",
    component: departmentAssistant,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },

  {
    path: "/dashboard/setup-ai/bots",
    name: "AI Assistant",
    component: BotList,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
    requiredPermission: "ai_assistant",
  },

  {
    path: "/dashboard/setup-ai/bots/knowledge-base/bot/:bot",
    component: KnowledgeBase,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },

  {
    path: "/dashboard/setup-ai/interactions",
    component: Interactions,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },

  {
    path: "/dashboard/setup-ai/bots/knowledge-base/:knowledgeBaseId/bot/:bot/trainings",
    component: TrainingList,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    protected: true,
    redirectPath: "/login",
  },
  {
    path: "/login/verify/user/:userId",
    component: TwoFactorVerify,
    layout: AuthLayout,
  },
  {
    path: "/login/recruiter/verifyOtp/:userId",
    component: CompanyRecruiterVerify,
    layout: AuthLayout,
  },
  {
    path: "/getstarted/:userType/verify/:userId",
    component: flow,
  },
  {
    path: "/getstarted/:userType?",
    component: flow,
  },
  // Legacy redirects — keep old /start/flow/* links working
  {
    path: "/start/flow/candidate/:step?/:userId?",
    component: React.lazy(() =>
      Promise.resolve({
        default: () => {
          const { Redirect } = require("react-router-dom");
          return React.createElement(Redirect, { to: "/getstarted/candidate" });
        },
      })
    ),
  },
  {
    path: "/start/flow/company/:step?/:userId?",
    component: React.lazy(() =>
      Promise.resolve({
        default: () => {
          const { Redirect } = require("react-router-dom");
          return React.createElement(Redirect, { to: "/getstarted/company" });
        },
      })
    ),
  },
  {
    path: "/start/flow",
    component: React.lazy(() =>
      Promise.resolve({
        default: () => {
          const { Redirect } = require("react-router-dom");
          return React.createElement(Redirect, { to: "/getstarted" });
        },
      })
    ),
  },

  {
    path: "/dashboard/setup-ai/assistant",
    component: AssistantScreen,
    layout: AdminLayout,
    allowedRoles: [USER_TYPES.EMPLOYER],
    exact: true,
    protected: true,
    redirectPath: "/login",
  },

  {
    path: "/dashboard/payment",
    component: PaymentPage,
    layout: AdminLayout,
    exact: true,
    allowedRoles: [USER_TYPES.EMPLOYER],
    redirectPath: "/login",
  },

  // REMOVED: /dashboard/upgrade route
  // Billing is now accessed through Company Profile Settings (Super Admin only)
  // See: CompanyProfile.js - Billing & Subscription section

  {
    path: "/dashboard/successPage",
    component: successPage,
    layout: AdminLayout,
    exact: true,
    allowedRoles: [USER_TYPES.EMPLOYER],
    redirectPath: "/login",
  },

  {
    path: "/dashboard/trial",
    component: trialPage,
    layout: AdminLayout,
    exact: true,
    allowedRoles: [USER_TYPES.EMPLOYER],
    redirectPath: "/login",
  },

  // ── Credit System ──
  {
    path: "/dashboard/credits",
    component: CreditDashboard,
    layout: AdminLayout,
    exact: true,
    allowedRoles: [USER_TYPES.EMPLOYER],
    redirectPath: "/login",
  },
  {
    path: "/dashboard/credits/boosters",
    component: BoosterPacks,
    layout: AdminLayout,
    exact: true,
    allowedRoles: [USER_TYPES.EMPLOYER],
    redirectPath: "/login",
  },
  {
    path: "/dashboard/billing",
    component: BillingManagement,
    layout: AdminLayout,
    exact: true,
    allowedRoles: [USER_TYPES.EMPLOYER],
    redirectPath: "/login",
  },

  // Public Jobs route
  {
    path: "/jobs",
    component: JobsPage,
    layout: WebsiteLayout,
    exact: true,
  },

  {
    path: "/admin",
    component: AdminPage,
    exact: true,
  },

  {
    path: "/admin/companies/:companyId",
    component: CompanyAnalytics,
    exact: true,
  },


  // ── Website Pages ──
  // NOTE: These routes are intentionally kept active in the CRA.
  // In production they will be intercepted by Azure Front Door path-based routing
  // and served by the Next.js marketing site BEFORE the request ever reaches this SPA.
  // DO NOT remove these until Azure Front Door routing is live and verified.
  {
    path: "/for-candidates",
    component: React.lazy(() => import("views/space/website/Pages/ForCandidatesPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/for-recruiters",
    component: React.lazy(() => import("views/space/website/Pages/ForRecruitersPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/pricing",
    component: React.lazy(() => import("views/space/website/Pages/PricingPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/integrations",
    component: React.lazy(() => import("views/space/website/Pages/IntegrationsPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/about",
    component: React.lazy(() => import("views/space/website/Pages/AboutPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/contact",
    component: React.lazy(() => import("views/space/website/Pages/ContactPage")),
    layout: WebsiteLayout,
    exact: true,
  },

  // ── SEO / AEO Pages ── (same note as above — kept until Front Door is live)
  {
    path: "/ai-resume-matching",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/AiResumeMatchingPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/candidate-application-tracking",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/CandidateApplicationTrackingPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/transparent-hiring",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/TransparentHiringPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/reduce-time-to-hire",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/ReduceTimeToHirePage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/structured-candidate-profiles",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/StructuredCandidateProfilesPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/keelzo-vs-greenhouse",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/KeelzoVsGreenhousePage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/keelzo-vs-lever",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/KeelzoVsLeverPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/keelzo-vs-workable",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/KeelzoVsWorkablePage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/keelzo-vs-notion-hiring",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/KeelzoVsNotionPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/best-ats-for-startups",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/BestAtsStartupsPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/best-hiring-software-early-stage",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/BestHiringEarlyStagePage")),
    layout: WebsiteLayout,
    exact: true,
  },

  // ── Blog Pages ── (same note — kept until Front Door is live)
  {
    path: "/blog/ai-resume-paradox",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/AiResumeParadoxPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/blog/stop-resume-overload",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/StopResumeOverloadPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/blog/improve-hiring-transparency",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/ImproveHiringTransparencyPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/blog/why-candidates-ghost-recruiters",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/WhyCandidatesGhostPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/blog/shorten-hiring-cycles",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/ShortenHiringCyclesPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/blog/why-ats-fail-candidates",
    component: React.lazy(() => import("views/space/website/SeoPages/wrappers/WhyAtsFailCandidatesPage")),
    layout: WebsiteLayout,
    exact: true,
  },

  // ── Legal Pages ── (same note — kept until Front Door is live)
  {
    path: "/privacy-policy",
    component: React.lazy(() => import("views/space/website/Legal/PrivacyPolicyPage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/terms-of-service",
    component: React.lazy(() => import("views/space/website/Legal/TermsOfServicePage")),
    layout: WebsiteLayout,
    exact: true,
  },
  {
    path: "/cookie-policy",
    component: React.lazy(() => import("views/space/website/Legal/CookiePolicyPage")),
    layout: WebsiteLayout,
    exact: true,
  },



  // ==========================================================================
  // FREE TIER CAREER PAGE ROUTE (Catch-All)
  // Must be at the very bottom so it doesn't hijack valid paths like /login
  // ==========================================================================
  {
    path: "/:slug",
    component: CareerPageWrapper,
    layout: React.Fragment,
    exact: true,
  },
];

export { AdminLayout };
export default routes;
